/**
 * Buyer-Demand Phase 2.5 — manual click flow client.
 *
 * Wraps `GET /api/manual/start/:id` (the JSON-discriminated entry
 * point) with a typed helper so the wall pages dispatch on response
 * shape without re-typing the union at every call site.
 *
 * The custom-question prescreen flow uses the `sessionId` returned
 * here as the key for `POST /api/manual-prescreen/answer`.
 */
import { API_BASE } from './apiBase';
import { supabase } from './supabase';

export interface CustomQuestionPayload {
  questionHash: string;
  question: string;
  type: 'single' | 'multi';
  options: string[];
  required: string[];
}

export interface ManualUnknownQual {
  qualCode: number;
  question: string;
  type: 'single' | 'multi' | 'range' | 'open';
  options: Array<{ code: string; label: string }>;
  required_codes?: string[];
  attributeKey?: string;
  exchange?: 'ps' | 'rfg' | 'manual';
  customQuestion?: CustomQuestionPayload;
}

export type ManualStartResponse =
  | { kind: 'redirect'; url: string }
  | { kind: 'prescreen_required'; sessionId: string; unknownQuals: ManualUnknownQual[] }
  | { kind: 'screenout'; reason?: string };

async function authHeader(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return `Bearer ${token}`;
}

/**
 * Fetches `/api/manual/start/:id`. Throws on network/auth failures so
 * the caller can show an error toast; resolves with the typed JSON
 * response on success.
 */
export async function startManualSurvey(surveyId: string): Promise<ManualStartResponse> {
  const Authorization = await authHeader();
  const res = await fetch(`${API_BASE}/api/manual/start/${encodeURIComponent(surveyId)}`, {
    headers: { Authorization },
  });
  if (!res.ok) throw new Error(`manual-start failed: ${res.status}`);
  return res.json();
}
